import { resolveOptions } from './defaults.js';
import { createState, type LoggerState } from './core/state.js';
import { createReentrancyGuard } from './core/reentrancy.js';
import { createDedupeStore, shouldSample } from './core/sampler.js';
import { createBreadcrumbBuffer, type BreadcrumbBuffer } from './core/breadcrumbs.js';
import { coerceMessage, errorToObject, safeStringify } from './util/serialize.js';
import { randomId } from './util/uuid.js';
import { hasWindow } from './util/env.js';
import { diagnostic } from './util/log-internal.js';
import { SDK_NAME, SDK_VERSION } from './version.js';
import { patchConsole, type PatchConsoleHandle } from './capture/console.js';
import { installGlobalHandlers, type InstallGlobalHandle } from './capture/globalErrors.js';
import { formatConsoleArgs } from './capture/format.js';
import { createFetchTransport } from './transport/transport.js';
import { createPayloadQueue, type PayloadQueue } from './transport/queue.js';
import { installUnloadFlush, type UnloadFlushHandle } from './transport/beacon.js';
import type {
  Breadcrumb,
  EvamLogger,
  FlushResult,
  LogLevel,
  LogPayload,
  LogSource,
  LoggerOptions,
  ResolvedLoggerOptions,
} from './types.js';

export type PayloadSink = {
  enqueue: (payload: LogPayload) => void;
  flush: (timeoutMs?: number) => Promise<FlushResult>;
  close: () => Promise<void>;
};

export interface CreateLoggerInternals {
  /**
   * Replace the default in-memory sink with a real transport. Used by tests
   * and by Phase 4 wiring that swaps in FetchTransport-backed queue.
   */
  setSink?: (sink: PayloadSink) => void;
  /** Snapshot of the in-memory sink — testing helper. */
  getInMemoryPayloads?: () => LogPayload[];
}

function createInMemorySink(): PayloadSink & { snapshot: () => LogPayload[] } {
  const buf: LogPayload[] = [];
  return {
    enqueue: (p) => {
      buf.push(p);
    },
    flush: () => Promise.resolve({ sent: buf.length, dropped: 0, retried: 0 }),
    close: () => Promise.resolve(),
    snapshot: () => buf.slice(),
  };
}

export function createLogger(initialOptions?: LoggerOptions): EvamLogger & CreateLoggerInternals {
  let resolved: ResolvedLoggerOptions | null = null;
  let state: LoggerState | null = null;
  let breadcrumbs: BreadcrumbBuffer | null = null;
  let dedupe: ReturnType<typeof createDedupeStore> | null = null;
  let sink: PayloadSink = createInMemorySink();
  let inMemorySink = sink as ReturnType<typeof createInMemorySink>;
  let consoleHandle: PatchConsoleHandle | null = null;
  let globalHandle: InstallGlobalHandle | null = null;
  let unloadHandle: UnloadFlushHandle | null = null;
  let payloadQueue: PayloadQueue | null = null;
  let userSinkOverride = false;
  const guard = createReentrancyGuard();

  function ensureReady(): { resolved: ResolvedLoggerOptions; state: LoggerState } | null {
    if (!resolved || !state) return null;
    return { resolved, state };
  }

  function buildPayload(
    level: LogLevel,
    source: LogSource,
    rawArgs: unknown[],
    overrides?: Partial<LogPayload>,
  ): LogPayload | null {
    const ctx = ensureReady();
    if (!ctx) return null;
    const { resolved: opts, state: s } = ctx;

    const first = rawArgs[0];
    const restArgs = rawArgs.slice(1);

    const message = coerceMessage(first, opts.maxMessageLength);
    const payload: LogPayload = {
      level,
      message,
      timestamp: new Date().toISOString(),
      source,
      sdk: { name: SDK_NAME, version: SDK_VERSION },
    };

    if (s.sessionId) payload.sessionId = s.sessionId;
    if (s.user !== undefined) payload.user = s.user;
    if (s.tenant !== undefined) payload.tenant = s.tenant;
    if (opts.app !== undefined) payload.app = opts.app;
    if (opts.environment !== undefined) payload.environment = opts.environment;
    if (opts.release !== undefined) payload.release = opts.release;
    if (s.context && Object.keys(s.context).length > 0) payload.context = { ...s.context };
    if (s.tags && s.tags.length > 0) payload.tags = [...s.tags];

    if (hasWindow()) {
      try {
        payload.url = window.location.href;
        payload.userAgent = navigator.userAgent;
        payload.viewport = { w: window.innerWidth, h: window.innerHeight };
      } catch {
        /* ignore */
      }
    }

    if (first instanceof Error) {
      const e = errorToObject(first);
      if (e.name) payload.errorName = e.name;
      if (e.message) payload.errorMessage = e.message;
      if (e.stack) payload.stack = e.stack;
    }

    if (restArgs.length > 0) {
      const serialized = restArgs.map((a) => {
        try {
          return JSON.parse(safeStringify(a, opts.maxArgsBytes));
        } catch {
          return safeStringify(a, opts.maxArgsBytes);
        }
      });
      payload.args = serialized;
    }

    if (opts.captureBreadcrumbs && breadcrumbs) {
      const snap = breadcrumbs.snapshot();
      if (snap.length > 0) payload.breadcrumbs = snap;
    }

    if (overrides) Object.assign(payload, overrides);
    return payload;
  }

  function emit(
    level: LogLevel,
    source: LogSource,
    rawArgs: unknown[],
    overrides?: Partial<LogPayload>,
  ): void {
    const ctx = ensureReady();
    if (!ctx) return;
    const { resolved: opts, state: s } = ctx;
    if (!s.enabled) return;
    if (!opts.enabled) return;
    if (!shouldSample(opts.sampleRate)) return;

    const payload = buildPayload(level, source, rawArgs, overrides);
    if (!payload) return;
    if (dedupe?.shouldDrop(payload)) return;

    if (opts.captureBreadcrumbs && breadcrumbs) {
      breadcrumbs.add({
        ts: payload.timestamp,
        category: source,
        level,
        message: payload.message,
      });
    }

    const handleError = (err: unknown, p: LogPayload): void => {
      try {
        opts.onError(err, p);
      } catch {
        /* swallow */
      }
    };

    const dispatch = (p: LogPayload | null): void => {
      if (!p) return;
      try {
        guard.run(() => sink.enqueue(p));
      } catch (err) {
        handleError(err, p);
      }
    };

    guard.run(() => {
      try {
        if (opts.beforeSend) {
          const result = opts.beforeSend(payload);
          if (result && typeof (result as Promise<unknown>).then === 'function') {
            (result as Promise<LogPayload | null>).then(
              (p) => dispatch(p),
              (err) => handleError(err, payload),
            );
          } else {
            dispatch(result as LogPayload | null);
          }
        } else {
          dispatch(payload);
        }
      } catch (err) {
        handleError(err, payload);
      }
    });
  }

  function publicEmit(
    level: LogLevel,
    source: LogSource,
    rawArgs: unknown[],
    overrides?: Partial<LogPayload>,
  ): void {
    if (guard.active()) return;
    emit(level, source, rawArgs, overrides);
  }

  const api: EvamLogger & CreateLoggerInternals = {
    init(options) {
      if (resolved) {
        diagnostic(true, 'init() called more than once; ignoring');
        return;
      }
      resolved = resolveOptions(options);
      const stateInit: {
        user?: string;
        tenant?: string;
        context?: Record<string, unknown>;
        tags?: string[];
        sessionId: string;
        enabled: boolean;
      } = {
        sessionId: randomId(),
        enabled: resolved.enabled,
      };
      if (resolved.user !== undefined) stateInit.user = resolved.user;
      if (resolved.tenant !== undefined) stateInit.tenant = resolved.tenant;
      if (resolved.context !== undefined) stateInit.context = resolved.context;
      if (resolved.tags !== undefined) stateInit.tags = resolved.tags;
      state = createState(stateInit);
      state.initialized = true;
      breadcrumbs = createBreadcrumbBuffer(resolved.maxBreadcrumbs);
      dedupe = createDedupeStore(resolved.dedupeWindowMs);

      if (!userSinkOverride) {
        if (resolved.fetch) {
          const transport = createFetchTransport(resolved as ResolvedLoggerOptions & { fetch: typeof fetch });
          const authRefresh = resolved.onAuthError
            ? async () => {
                await resolved!.onAuthError!();
              }
            : undefined;
          payloadQueue = createPayloadQueue(resolved, transport, authRefresh);
          sink = {
            enqueue: (p) => payloadQueue!.enqueue(p),
            flush: (timeoutMs) => payloadQueue!.flush(timeoutMs),
            close: () => payloadQueue!.close(),
          };
          if (resolved.flushOnUnload) {
            unloadHandle = installUnloadFlush(resolved as ResolvedLoggerOptions & { fetch: typeof fetch }, payloadQueue);
          }
        } else {
          diagnostic(resolved.debug, 'fetch is not available; using in-memory sink');
        }
      }

      const captureLevels = resolved.captureConsole;
      const anyConsoleEnabled =
        captureLevels.error || captureLevels.warn || captureLevels.info ||
        captureLevels.log || captureLevels.debug;
      if (anyConsoleEnabled && typeof console !== 'undefined') {
        consoleHandle = patchConsole(
          console as unknown as Parameters<typeof patchConsole>[0],
          captureLevels,
          (level, args) => {
            const opts = resolved;
            if (!opts) return;
            const formatted = formatConsoleArgs(args, {
              maxMessageLength: opts.maxMessageLength,
              maxArgsBytes: opts.maxArgsBytes,
            });
            const overrides: Partial<import('./types.js').LogPayload> = {};
            if (formatted.errorName) overrides.errorName = formatted.errorName;
            if (formatted.errorMessage) overrides.errorMessage = formatted.errorMessage;
            if (formatted.stack) overrides.stack = formatted.stack;
            if (formatted.args) overrides.args = formatted.args;
            emit(level, 'console', [formatted.message], overrides);
          },
          guard.run,
          guard.active,
        );
      }

      if (resolved.captureGlobalErrors && hasWindow()) {
        globalHandle = installGlobalHandlers(
          window as unknown as Parameters<typeof installGlobalHandlers>[0],
          (event) => {
            const overrides: Partial<import('./types.js').LogPayload> = {};
            if (event.errorName) overrides.errorName = event.errorName;
            if (event.errorMessage) overrides.errorMessage = event.errorMessage;
            if (event.stack) overrides.stack = event.stack;
            if (event.filename) overrides.filename = event.filename;
            if (event.lineno !== undefined) overrides.lineno = event.lineno;
            if (event.colno !== undefined) overrides.colno = event.colno;
            emit('error', event.source, [event.message], overrides);
          },
          guard.run,
          guard.active,
        );
      }

      diagnostic(resolved.debug, 'initialized', { baseUrl: resolved.baseUrl });
    },

    isInitialized: () => Boolean(resolved && state?.initialized),

    async destroy() {
      consoleHandle?.unpatch();
      consoleHandle = null;
      globalHandle?.uninstall();
      globalHandle = null;
      unloadHandle?.uninstall();
      unloadHandle = null;
      try {
        await sink.close();
      } catch {
        /* swallow */
      }
      payloadQueue = null;
      resolved = null;
      state = null;
      breadcrumbs = null;
      dedupe = null;
      sink = createInMemorySink();
      inMemorySink = sink as ReturnType<typeof createInMemorySink>;
      userSinkOverride = false;
    },

    error: (message, ...args) => publicEmit('error', 'manual', [message, ...args]),
    warn: (message, ...args) => publicEmit('warn', 'manual', [message, ...args]),
    info: (message, ...args) => publicEmit('info', 'manual', [message, ...args]),
    log: (message, ...args) => publicEmit('log', 'manual', [message, ...args]),
    debug: (message, ...args) => publicEmit('debug', 'manual', [message, ...args]),

    capture(p) {
      const args = p.message !== undefined ? [p.message] : [];
      const overrides: Partial<LogPayload> = { ...p };
      delete (overrides as { message?: string }).message;
      delete (overrides as { level?: LogLevel }).level;
      publicEmit(p.level, p.source ?? 'manual', args, overrides);
    },

    setUser(user) {
      if (!state) return;
      if (user === null) delete state.user;
      else state.user = user;
    },

    setTenant(tenant) {
      if (!state) return;
      if (tenant === null) delete state.tenant;
      else state.tenant = tenant;
    },

    setContext(patch) {
      if (!state) return;
      state.context = { ...(state.context ?? {}), ...patch };
    },

    clearContext() {
      if (!state) return;
      delete state.context;
    },

    setTags(tags) {
      if (!state) return;
      state.tags = [...tags];
    },

    addBreadcrumb(b: Breadcrumb) {
      if (breadcrumbs) breadcrumbs.add(b);
    },

    flush(timeoutMs) {
      return sink.flush(timeoutMs);
    },

    setEnabled(on) {
      if (state) state.enabled = on;
    },

    setSink(s) {
      sink = s;
      userSinkOverride = true;
    },

    getInMemoryPayloads: () => inMemorySink.snapshot(),
  };

  if (initialOptions) {
    api.init(initialOptions);
  }
  return api;
}
